import type { AgentTurnCoverage, AgentTurnFilterQuery } from '@avisoma/shared';

export interface ChatQuestionsFilters {
  /** Selected coverages; empty means every coverage. */
  coverage: AgentTurnCoverage[];
  /** Only turns with a partial reason or an error. */
  partialOnly: boolean;
  projectId: string;
  /** `YYYY-MM-DD` from a date input, inclusive; '' for no bound. */
  from: string;
  /** `YYYY-MM-DD` from a date input, inclusive; '' for no bound. */
  to: string;
}

// The turns worth reading are the ones the chat could not fully answer.
export const DEFAULT_FILTERS: ChatQuestionsFilters = {
  coverage: ['no_data', 'partial'],
  partialOnly: false,
  projectId: '',
  from: '',
  to: '',
};

export const FILTER_STORAGE_KEY = 'chat-questions-filters';

/** Local midnight of a `YYYY-MM-DD` date, as an ISO timestamp. */
function startOfDayIso(date: string, addDays = 0): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day + addDays).toISOString();
}

/**
 * Maps the UI filters onto the endpoint query. The date inputs are inclusive days in the
 * admin's time zone; the API takes `from` inclusive and `to` exclusive, so `to` becomes the
 * next local midnight.
 */
export function toFilterQuery(filters: ChatQuestionsFilters): AgentTurnFilterQuery {
  const projectId = filters.projectId.trim();
  return {
    ...(filters.coverage.length > 0 && { coverage: filters.coverage }),
    ...(filters.partialOnly && { partial: 'true' }),
    ...(projectId && { projectId }),
    ...(filters.from && { from: startOfDayIso(filters.from) }),
    ...(filters.to && { to: startOfDayIso(filters.to, 1) }),
  };
}

export function hasActiveFilters(filters: ChatQuestionsFilters): boolean {
  return (
    filters.coverage.length > 0 ||
    filters.partialOnly ||
    filters.projectId.trim() !== '' ||
    !!filters.from ||
    !!filters.to
  );
}
